import { useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'store/hooks';
import { selectReceivingFieldsByMid } from 'features/system-stats/systemStatsSlice';

export const LAYER_L = 0;
export const LAYER_M = 1;
export const LAYER_H = 2;

export type SimulcastLayer = typeof LAYER_L | typeof LAYER_M | typeof LAYER_H;

export type UseObservedSimulcastLayersResult = {
  activeLayer: SimulcastLayer | undefined;
  disabledLayers: Set<SimulcastLayer>;
  probeLayer: (layer: SimulcastLayer) => void;
};

// Wall-clock grace window between probing a layer and disabling it. Long
// enough for an SFU substream switch to materialize, short enough that a
// stalled probe disables before users start clicking again.
const PROBE_GRACE_MS = 4000;

// Janus's typical 1:2:4 scale-down across H/M/L places camera output around
// 720/360/180 or 1080/540/270; the thresholds below partition both ranges.
const HIGH_LAYER_MIN_HEIGHT = 600;
const MEDIUM_LAYER_MIN_HEIGHT = 300;

const bucketFromHeight = (height: number): SimulcastLayer => {
  if (height >= HIGH_LAYER_MIN_HEIGHT) {
    return LAYER_H;
  }
  if (height >= MEDIUM_LAYER_MIN_HEIGHT) {
    return LAYER_M;
  }
  return LAYER_L;
};

const videoFrameHeight = (
  fields: { kind: 'audio' | 'video'; frameHeight?: number } | undefined
): number | undefined => {
  if (!fields || fields.kind !== 'video') {
    return undefined;
  }
  return fields.frameHeight;
};

export const useObservedSimulcastLayers = (mid: string): UseObservedSimulcastLayersResult => {
  const fields = useAppSelector((state) => selectReceivingFieldsByMid(state, mid));
  const [observedLayers, setObservedLayers] = useState<Set<SimulcastLayer>>(() => new Set());
  // Probes don't need to render; only disabledLayers does, and that recomputes
  // each render from observedLayers + Date.now() (a ref click avoids the extra
  // render that useState would force).
  const pendingProbesRef = useRef<Map<SimulcastLayer, number>>(new Map());

  // Reset feed-scoped state when the subscriber switches to a new mid. Without
  // this, observations from the previous feed leak into the new feed's
  // disabledLayers logic — a probe of a layer the new feed never publishes
  // would silently stay enabled forever because the prior feed once published
  // that resolution.
  //
  // The reset lives in the cleanup so it only fires on mid change (or unmount),
  // not on first render — `useState` already starts both stores empty.
  useEffect(
    () => () => {
      setObservedLayers(new Set());
      pendingProbesRef.current = new Map();
    },
    [mid]
  );

  useEffect(() => {
    const height = videoFrameHeight(fields);
    if (height === undefined) {
      return;
    }
    const layer = bucketFromHeight(height);
    setObservedLayers((prev) => {
      if (prev.has(layer)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(layer);
      return next;
    });
  }, [fields]);

  const currentHeight = videoFrameHeight(fields);
  const activeLayer = currentHeight === undefined ? undefined : bucketFromHeight(currentHeight);

  const disabledLayers = new Set<SimulcastLayer>();
  const now = Date.now();
  pendingProbesRef.current.forEach((probedAtMs, layer) => {
    if (now - probedAtMs >= PROBE_GRACE_MS && !observedLayers.has(layer)) {
      disabledLayers.add(layer);
    }
  });

  const probeLayer = (layer: SimulcastLayer) => {
    pendingProbesRef.current.set(layer, Date.now());
  };

  return { activeLayer, disabledLayers, probeLayer };
};
